// Mapa estático de un punto único — útil para mostrar la ubicación del
// negocio en el detalle de una orden o de un cliente.
//
// Características:
//   - Centrado en (lat, lon), zoom fijo 16 (calle).
//   - Scroll del ratón desactivado (no roba el scroll del drawer que
//     contiene el mapa).
//   - Marker con la hormiga del brand (estilo de los pines de entrega)
//     o un ícono alternativo de negocio.
//   - Si lat/lon son nulos, renderiza un placeholder con copy útil.

import { useEffect } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Color de fondo del pin del marker. Lo declaramos arriba como
// constante para que el SVG pueda usarlo como "color del agujero"
// (puerta/ventana del negocio, rayas del awning) y se vea como huecos
// sobre la silueta blanca del ícono.
const PIN_BG = "#6A1B9A"; // morado profundo (Material purple 800)

// Icono "negocio / kiosko" — storefront estilizado centrado en el
// viewBox para que se lea bien en marker grande:
//   - Awning trapezoidal con rayas verticales (toldo a rayas típico
//     de bodega/quiosco peruano).
//   - Cuerpo del local en silueta blanca.
//   - Mostrador grande horizontal con división vertical.
//   - Puerta lateral con manija circular.
//   - Letrero rectangular sobre el awning (sugerido como "negocio").
//
// La tinta principal va por `currentColor` (el caller pasa blanco);
// los "huecos" (mostrador, puerta, rayas del awning) usan PIN_BG para
// verse como agujeros sobre el fondo del pin morado.
const ANT_SVG = `
<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
  <!-- Letrero sobre el awning (sugiere "NEGOCIO") -->
  <rect x="22" y="4" width="20" height="6" rx="1" fill="currentColor"/>
  <!-- 3 puntos horizontales sobre el letrero (placeholder de texto) -->
  <circle cx="28" cy="7" r="0.9" fill="${PIN_BG}"/>
  <circle cx="32" cy="7" r="0.9" fill="${PIN_BG}"/>
  <circle cx="36" cy="7" r="0.9" fill="${PIN_BG}"/>

  <!-- Awning trapezoidal ancho -->
  <path d="M2 22 L8 12 L56 12 L62 22 Z" fill="currentColor"/>
  <!-- Rayas verticales del awning -->
  <line x1="12" y1="12" x2="10" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>
  <line x1="20" y1="12" x2="18" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>
  <line x1="28" y1="12" x2="27" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>
  <line x1="36" y1="12" x2="37" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>
  <line x1="44" y1="12" x2="46" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>
  <line x1="52" y1="12" x2="54" y2="22" stroke="${PIN_BG}" stroke-width="1.6"/>

  <!-- Cuerpo del local -->
  <rect x="6" y="22" width="52" height="38" fill="currentColor"/>

  <!-- Mostrador / ventana grande -->
  <rect x="10" y="28" width="44" height="14" fill="${PIN_BG}"/>
  <!-- División vertical central -->
  <line x1="32" y1="28" x2="32" y2="42" stroke="currentColor" stroke-width="1.2"/>
  <!-- Tabla del mostrador -->
  <rect x="10" y="44" width="44" height="2" fill="currentColor"/>

  <!-- Puerta lateral derecha (hueco con manija) -->
  <rect x="40" y="48" width="12" height="12" fill="${PIN_BG}"/>
  <circle cx="50" cy="54" r="1.1" fill="currentColor"/>

  <!-- Cajón / cajita lateral izquierda (sugerencia de mercadería) -->
  <rect x="10" y="50" width="14" height="10" fill="${PIN_BG}"/>
  <line x1="17" y1="50" x2="17" y2="60" stroke="currentColor" stroke-width="0.8"/>
  <line x1="10" y1="55" x2="24" y2="55" stroke="currentColor" stroke-width="0.8"/>
</svg>
`.trim();

// Pin del negocio: círculo naranja con la hormiga blanca encima.
// 40x40 para que se lea bien sobre el tile de OSM a zoom calle.
function businessAntIcon(): L.DivIcon {
  const html = `
    <div style="
      width: 56px; height: 56px;
      background: ${PIN_BG};
      border: 3px solid #FFFFFF;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 3px 8px rgba(0,0,0,0.4);
      color: #FFFFFF;
    ">
      <div style="width: 38px; height: 38px;">${ANT_SVG}</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: "",
    iconSize: [56, 56],
    iconAnchor: [28, 28],
  });
}

type Props = {
  lat?: number | null;
  lon?: number | null;
  /** Altura del contenedor del mapa. Default 200px. */
  height?: number;
  /** Etiqueta corta arriba (ej. "Ubicación del negocio"). */
  label?: string;
  /** Subtítulo opcional, ej. dirección legible. */
  subLabel?: string;
};

export function LocationMiniMap({
  lat,
  lon,
  height = 200,
  label,
  subLabel,
}: Props) {
  if (lat == null || lon == null) {
    return (
      <div className="rounded-md border border-dashed border-border bg-page/60 p-4">
        {label && (
          <div className="text-[11px] uppercase tracking-wide text-sub mb-1">
            {label}
          </div>
        )}
        <div className="text-xs text-sub italic">
          Este negocio no tiene coordenadas registradas — no se puede ubicar en el mapa.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="text-[11px] uppercase tracking-wide text-sub">
          {label}
        </div>
      )}
      <div
        className="rounded-md overflow-hidden border border-border"
        style={{ height }}
      >
        <MapContainer
          center={[lat, lon]}
          zoom={16}
          // Sin scroll-zoom para no robar el scroll del drawer/sidebar
          // donde el mapa está embebido. Mantenemos drag y click para
          // que el supervisor pueda explorar la cuadra si quiere.
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={22}
            maxNativeZoom={19}
          />
          <RecenterOnChange lat={lat} lon={lon} />
          <Marker position={[lat, lon]} icon={businessAntIcon()} />
        </MapContainer>
      </div>
      {subLabel && (
        <div className="text-[11px] text-sub flex items-start gap-1">
          <span>📍</span>
          <span className="leading-snug">{subLabel}</span>
        </div>
      )}
      <div className="text-[10px] text-sub font-mono">
        {lat.toFixed(5)}, {lon.toFixed(5)}
      </div>
    </div>
  );
}

/// Recenter del mapa cuando cambian las coords (al pasar de una orden
/// a otra dentro del mismo drawer sin desmontar el componente).
function RecenterOnChange({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], 16);
  }, [lat, lon, map]);
  return null;
}
